
 // src/pages/product/config.js
import React from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  background: #f8fafc;
`;

const ProgressContainer = styled.div`
  margin-bottom: 32px;
  padding: 20px;
  background: white;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 500;
  color: #374151;
`;

const ProgressBarOuter = styled.div`
  height: 10px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressBarInner = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  border-radius: 999px;
  transition: width 0.6s ease-out;
  min-width: 2%;
  ${props => props.$isComplete && `
    background: linear-gradient(90deg, #10b981, #34d399);
  `}
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 24px;
`;

const ConfigSection = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #cbd5e0;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  }
`;

const SectionHeader = styled.div`
  padding: 16px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SectionIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #4a5568;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
`;

const SectionTitle = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const SectionProgress = styled.div`
  padding: 12px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const SectionProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 6px;
`;

const MiniProgressOuter = styled.div`
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
`;

const MiniProgressInner = styled.div`
  height: 100%;
  background: #6366f1;
  border-radius: 3px;
  transition: width 0.5s ease;
`;

const ConfigList = styled.div``;

const ConfigItem = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.15s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #f8fafc;
  }
`;

const ItemContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 999px;
  margin-bottom: 4px;
  
  ${props => props.$completed ? `
    background: #d1fae5;
    color: #065f46;
  ` : `
    background: #fef3c7;
    color: #92400e;
  `}
`;

const CheckIcon = styled.span`
  font-size: 14px;
`;

const ItemName = styled.div`
  font-size: 14.5px;
  font-weight: 500;
  color: #1f2937;
`;

const ItemDescription = styled.div`
  font-size: 12.5px;
  color: #6b7280;
  line-height: 1.45;
`;

const ItemAction = styled.div`
  color: #9ca3af;
  font-size: 20px;
  transform: rotate(-90deg);
  transition: all 0.2s ease;
  
  ${ConfigItem}:hover & {
    color: #4b5563;
    transform: rotate(0deg);
  }
`;

const Config = ({ product, productSystemRef }) => {
  const history = useHistory();


  // Define the configs array (moved from ViewProduct)
  const configs = [
    {
      label: 'Add Benefit',
      path: `/dashboard/setup/product/${productSystemRef}/benefit`,
      description: 'Configure product features and benefit packages',
      section: 'Product & Pricing',
      icon: '💰',
      completed: product?.productBenefits?.length > 0
    },
    {
      label: 'Policy Number Config',
      path: `/dashboard/setup/product/${productSystemRef}/policyno`,
      description: 'Configure policy numbering schemes and formats',
      section: 'Policy Management',
      icon: '📋',
      completed:  !!product?.policyNumberConfig?.policyNumConfigcategory,
    },
    {
      label: 'Excess Config',
      path: `/dashboard/setup/product/${productSystemRef}/excess`,
      description: 'Define excess amounts and liability limits',
      section: 'Insurance Certificates',
      icon: '📄',
      completed: !!product?.excessConfig?.standard?.maximum
    },
    {
      label: 'Payment Config',
      path: `/dashboard/setup/product/${productSystemRef}/payment`,
      description: 'Manage payment methods and processing settings',
      section: 'Financial Systems',
      icon: '💳',
      completed:  !!Object.keys(product?.productPaymentConfig ?? {}).length === 0,
    },
    {
      label: 'Motor Certificate Config',
      path: `/dashboard/setup/product/${productSystemRef}/motorcert`,
      description: 'Set up motor insurance certificate templates',
      section: 'Insurance Certificates',
      icon: '📄',
      completed: !!Object.keys(product?.productCertificateConfig ?? {}).length === 0,
    },
    {
      label: 'Product Policy Config',
      path: `/dashboard/setup/product/${productSystemRef}/productpolicy`,
      description: 'Set up policy terms, conditions, and workflows',
      section: 'Policy Management',
      icon: '📋',
      completed: !!product?.productPolicyConfig.covernoteIssuance
    },
    {
      label: 'Requirements Config',
      path: `/dashboard/setup/product/${productSystemRef}/requirements`,
      description: 'Set up policy requirements',
      section: 'Policy Management',
      icon: '📋',
      completed: product?.productRequirements.length > 0,
    },
    {
      label: 'Terms & Conditions',
      path: `/dashboard/setup/product/${productSystemRef}/tnc`,
      description: 'Configure Your Terms and Conditions',
      section: 'Policy Management',
      icon: '📋',
      completed:  !!product?.productTnc.uploadPath
    },
    {
      label: 'Accounting Config',
      path: `/dashboard/setup/product/${productSystemRef}/accounting`,
      description: 'Configure accounting integration and reporting',
      section: 'Financial Systems',
      icon: '💳',
      completed: product?.productAccountingConfigRef.length > 5
    },
    {
      label: 'Premium Pricing',
      path: `/dashboard/setup/product/${productSystemRef}/premiumpricing`,
      description: 'Configure premium calculation rules and rates',
      section: 'Product & Pricing',
      icon: '💰',
      completed: product.productPremiumPricingConfigref !== 'n/a',
    },
      {
          label: 'Valuation Config',
          path: `/dashboard/setup/product/${productSystemRef}/valuation`,
          description: 'Configure Valuation Rules',
          section: 'Insurance Certificates',
          icon: '💰',
          completed: !!product?.productValuationConfig?.isValuationRequired
      },
      {
          label: 'Document Configuration',
          path: `/dashboard/setup/product/${productSystemRef}/document`,
          description: 'Configure Document management',
          section: 'Policy Management',
          icon: '📋',
          completed: "",
      },
      // {
      //     label: 'Cover Details config',
      //     path: `/dashboard/setup/product/${productSystemRef}/coverdetails`,
      //     description: 'Configure Product Cover Details',
      //     section: 'Policy Management',
      //     icon: '📋',
      // },
      {
          label: 'Liability Config',
          path: `/dashboard/setup/product/${productSystemRef}/liability`,
          description: 'Configure Product Liabilities',
          section: 'Insurance Certificates',
          icon: '💰',
          completed: !!Object.keys(product?.product?.productLiability ?? {}).length === 0
      }
  ];

  // Group configs by section
  const configSections = [
    {
      title: 'Policy Management',
      icon: '📋',
      configs: configs.filter(config => config.section === 'Policy Management')
        .map(config => ({
          name: config.label,
          path: config.path,
          description: config.description,
          completed: config.completed,
        }))
    },
    {
      title: 'Product & Pricing',
      icon: '💰',
      configs: configs.filter(config => config.section === 'Product & Pricing')
        .map(config => ({
          name: config.label,
          path: config.path,
          description: config.description,
          completed: config.completed,
        }))
    },
    {
      title: 'Insurance Certificates',
      icon: '📄',
      configs: configs.filter(config => config.section === 'Insurance Certificates')
        .map(config => ({
          name: config.label,
          path: config.path,
          description: config.description,
          completed: config.completed,
        }))
    },
    {
      title: 'Financial Systems',
      icon: '💳',
      configs: configs.filter(config => config.section === 'Financial Systems')
        .map(config => ({
          name: config.label,
          path: config.path,
          description: config.description,
          completed: config.completed,

        }))
    },
  ];

  // ── Overall progress ───────────────────────────────────────
  const total = configs.length;
  const completedCount = configs.filter(c => !!c.completed).length;
  const overallPercent = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  return (
    <Container>
      <ProgressContainer>
        <ProgressHeader>
          <span>Product Configuration Progress</span>
          <span>{completedCount} / {total} — {overallPercent}%</span>
        </ProgressHeader>
        <ProgressBarOuter>
          <ProgressBarInner
            style={{ width: `${overallPercent}%` }}
            $isComplete={overallPercent === 100}
          />
        </ProgressBarOuter>
      </ProgressContainer>

      <DashboardGrid>
        {configSections.map((section, sectionIndex) => {
          const secTotal = section.configs.length;
          const secCompleted = section.configs.filter(c => c.completed).length;
          const secPercent = secTotal > 0 ? Math.round((secCompleted / secTotal) * 100) : 0;

          return (
            <ConfigSection key={sectionIndex}>
              <SectionHeader>
                <SectionIcon>{section.icon}</SectionIcon>
                <SectionTitle>{section.title}</SectionTitle>
              </SectionHeader>

              <SectionProgress>
                <SectionProgressLabel>
                  <span>Section progress</span>
                  <span>{secCompleted}/{secTotal}</span>
                </SectionProgressLabel>
                <MiniProgressOuter>
                  <MiniProgressInner style={{ width: `${secPercent}%` }} />
                </MiniProgressOuter>
              </SectionProgress>

              <ConfigList>
                {section.configs.map((item, itemIndex) => (
                  <ConfigItem
                    key={itemIndex}
                    onClick={() => history.push(item.path)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        history.push(item.path);
                      }
                    }}
                  >
                    <ItemContent>
                      <StatusBadge $completed={!!item.completed}>
                        {item.completed ? (
                          <>
                            <CheckIcon>✓</CheckIcon> Completed
                          </>
                        ) : (
                          'Pending'
                        )}
                      </StatusBadge>
                      <ItemName>{item.name}</ItemName>
                      <ItemDescription>{item.description}</ItemDescription>
                    </ItemContent>
                    <ItemAction>→</ItemAction>
                  </ConfigItem>
                ))}
              </ConfigList>
            </ConfigSection>
          );
        })}
      </DashboardGrid>
    </Container>
  );
};

export default Config;